import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, interval, Subscription } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { NotificationService } from './notification.service';
import { ConfigurationManagerService } from './configuration-manager.service';
import { IncidentService } from './incident.service';
import { Router } from '@angular/router';
import { PapiAuthService } from './papi-auth.service';

export interface IncidentResponse {
  count: number;
  rows: Incident[];
}

export interface Incident {
  id: string;
  alertId: string | null;
  organizationIncidentCategoryId: string | null;
  name: string;
  description: string;
  status: string;
  type: string;
  public: boolean;
  endAt: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class RealtimeService {
  private destroy$ = new Subject<void>();
  private pollingInterval = 5000; // 5 seconds
  private incidentPollingSubscription: Subscription | null = null;

  // Connection status
  private connectionStatus$ = new BehaviorSubject<'connected' | 'disconnected' | 'connecting'>('disconnected');
  public connectionStatus = this.connectionStatus$.asObservable();

  // Real-time data streams
  private incidents$ = new Subject<Incident>();
  private allIncidents$ = new BehaviorSubject<Incident[]>([]);
  
  public incidents = this.incidents$.asObservable();
  public allIncidents = this.allIncidents$.asObservable();

  // Store last known data to detect new items
  private lastIncidents: Incident[] = [];
  private isInitialLoad = true;

  // Add property to store latest API response
  private latestApiResponse$ = new BehaviorSubject<IncidentResponse | null>(null);
  public latestApiResponse = this.latestApiResponse$.asObservable();

  constructor(
    private notificationService: NotificationService,
    private incidentService: IncidentService,
    private authService: PapiAuthService,
    private router: Router
  ) {}

  // Get the current incidents
  getCurrentIncidents(): Incident[] {
    return this.allIncidents$.value;
  }

  // Start polling for real-time updates
  connect(): void {
    if (this.incidentPollingSubscription) {
      return; // Already connected
    }

    this.connectionStatus$.next('connecting');

    this.fetchIncidents();

    this.startIncidentPolling();
    
    this.connectionStatus$.next('connected');
  }

  // Stop polling
  disconnect(): void {
    
    if (this.incidentPollingSubscription) {
      this.incidentPollingSubscription.unsubscribe();
      this.incidentPollingSubscription = null;
    }

    this.connectionStatus$.next('disconnected');
  }

  // Fetch incidents from API
   fetchIncidents(): void {
    this.incidentService
      .getIncidentFindAndCountAllFromOrganization()
      .subscribe({
        next: (response: IncidentResponse) => {
          this.handleIncidentResponse(response, this.isInitialLoad);
          this.isInitialLoad = false;
        },
        error: (error) => {
          // console.error('Error fetching incidents from service:', error);
          if (error.error && (error.error.name === 'TokenExpiredError' || error.error.error?.name === 'TokenExpiredError')) {
            this.authService.clearCredentials();
            this.router.navigate(['/login']);
          }
          this.connectionStatus$.next('disconnected');
        }
      });
  }

  // Start polling incidents endpoint
  private startIncidentPolling(): void {
   
    this.incidentPollingSubscription = interval(this.pollingInterval)
      .pipe(
        switchMap(() => {
          return this.incidentService.getIncidentFindAndCountAllFromOrganization();
        }),
        catchError(error => {
          console.error('Error polling incidents from service:', error);
          this.connectionStatus$.next('disconnected');
          // Return empty result on error to continue polling
          return [{ count: 0, rows: [] }];
        })
      )
      .subscribe({
        next: (response: IncidentResponse) => {
          this.handleIncidentResponse(response);
        },
        error: (error) => {
          console.error('Subscription error:', error);
          this.connectionStatus$.next('disconnected');
        }
      });
  }

  // Handle incident response and detect new incidents
  private handleIncidentResponse(response: IncidentResponse, isInitial: boolean = false): void {
    // Validate the response first
    const validatedResponse = this.validateIncidentResponse(response);
    
    if (!validatedResponse || !validatedResponse.rows) {
      return;
    }

    // Store the latest API response
    this.latestApiResponse$.next(validatedResponse);

    const currentIncidents = validatedResponse.rows;
   
    this.allIncidents$.next(currentIncidents);

    // Create notification showing latest API response summary
    //this.createApiResponseNotification(validatedResponse, isInitial);

    if (!isInitial && this.lastIncidents.length > 0) {
      // Find new incidents (incidents that weren't in the last response)
      const newIncidents = currentIncidents.filter(currentIncident => 
        !this.lastIncidents.some(lastIncident => lastIncident.id === currentIncident.id)
      );

      // Find updated incidents (incidents that were modified)
      const updatedIncidents = currentIncidents.filter(currentIncident => {
        const lastIncident = this.lastIncidents.find(last => last.id === currentIncident.id);
        return lastIncident && lastIncident.updatedAt !== currentIncident.updatedAt;
      });

      
      newIncidents.forEach(incident => {
        this.handleNewIncident(incident);
      });

      // Handle updated incidents
      updatedIncidents.forEach(incident => {
        this.handleUpdatedIncident(incident);
      });

      // Find removed incidents (incidents that were in lastIncidents but not in currentIncidents)
      const removedIncidents = this.lastIncidents?.filter(lastIncident => 
        !currentIncidents.some(currentIncident => currentIncident?.id === lastIncident?.id)
      );

      // Handle removed incidents - delete their notifications
      removedIncidents.forEach(incident => {
        this.handleRemovedIncident(incident);
      });
    } else if (isInitial && currentIncidents.length > 0) {
      // On initial load, create detailed notifications for all active incidents
      this.createDetailedIncidentNotifications(currentIncidents);
    } 
    else if (!isInitial && this.lastIncidents?.length === 0 && currentIncidents?.length > 0) {
      // Handle case where we had no incidents before, but now we have some (first incident after empty state)
      currentIncidents.forEach(incident => {
        this.handleNewIncident(incident);
      });
    }

    // Store current incidents for next comparison
    this.lastIncidents = [...currentIncidents];
  }

  // New method to create API response summary notification
 

  // New method to create detailed notifications for each incident
  private createDetailedIncidentNotifications(incidents: Incident[]): void {
    incidents.forEach((incident, index) => {
      const isUrgent = incident.status === 'ACTIVE' && index < 2; // Make first 2 active incidents urgent
      const createdDate = new Date(incident.createdAt);
      const updatedDate = new Date(incident.updatedAt);
      const data=incident;
      const detailedContent = `${incident.name}
Description: ${incident.description || 'No description'}
Status: ${incident.status}
Type: ${incident.type}
Created: ${createdDate}
Updated: ${updatedDate}`;

      const notificationType = this.getIncidentNotificationType(incident.status);
      
      // Use switch statement for better type safety
      switch (notificationType) {
        case 'danger':
          this.notificationService.addDangerNotification(detailedContent, {
            id: incident.id,
            moveToTop: isUrgent,
            onGoLive: () => this.navigateToIncident(incident.id),
            replace: false
          });
          break;
          
        case 'warning':
          this.notificationService.addWarningNotification(detailedContent, {
            id: incident.id,
            moveToTop: false,
            onGoLive: () => this.navigateToIncident(incident.id),
            replace: false
          });
          break;
          
        case 'success':
          this.notificationService.addSuccessNotification(detailedContent, {
            id: incident.id,
            moveToTop: false,
            onGoLive: () => this.navigateToIncident(incident.id),
            replace: false
          });
          break;
          
        default:
          this.notificationService.addInfoNotification(detailedContent, {
            id: incident.id,
            moveToTop: false,
            onGoLive: () => this.navigateToIncident(incident.id),
            replace: false
          });
          break;
      }
    });
  }

  // Method to show detailed API response as notification
  private showDetailedApiResponse(): void {
    const latestResponse = this.latestApiResponse$.value;
    
    if (!latestResponse) {
      this.notificationService.addWarningNotification('No API response data available', {
        moveToTop: true,
        duration: 3000
      });
      return;
    }

    let detailedContent = `Latest API Response Details:
Total Count: ${latestResponse.count}
Timestamp: ${new Date().toLocaleString()}

Incidents:`;

    latestResponse.rows.forEach((incident, index) => {
      detailedContent += `
${index + 1}. ${incident.name}
   ID: ${incident.id}
   Status: ${incident.status}
   Type: ${incident.type}
   Created: ${new Date(incident.createdAt).toLocaleString()}`;
    });

    this.notificationService.addInfoNotification(detailedContent, {
      id: 'detailed-api-response',
      moveToTop: true,
      replace: true,
      duration: 10000 // Show longer for detailed view
    });
  }

  // Method to get latest API response
  getLatestApiResponse(): IncidentResponse | null {
    return this.latestApiResponse$.value;
  }

  // Method to manually show latest API data as notifications
  showLatestApiDataAsNotifications(): void {
    const latestResponse = this.latestApiResponse$.value;
    
    if (!latestResponse) {
      // If no cached data, fetch fresh data
      this.fetchIncidentsAndNotify();
      return;
    }

    
    // Create summary notification
    //this.createApiResponseNotification(latestResponse, false);
    
    // Create detailed notifications for active incidents
    const activeIncidents = latestResponse.rows.filter(incident => incident.status === 'ACTIVE');
    if (activeIncidents.length > 0) {
      this.createDetailedIncidentNotifications(activeIncidents);
    }
  }

  // Method to fetch incidents and immediately show as notifications
  fetchIncidentsAndNotify(): void {
    
    this.notificationService.addInfoNotification('Fetching latest incident data...', {
      id: 'fetching-data',
      moveToTop: true,
      duration: 2000
    });
    
    this.incidentService
      .getIncidentFindAndCountAllFromOrganization()
      .subscribe({
        next: (response: IncidentResponse) => {
          
          // Remove fetching notification
          this.notificationService.deleteNotification('fetching-data');
          
          // Process and show notifications
          this.handleIncidentResponse(response, false);
          
          // Show success message
          this.notificationService.addSuccessNotification(
            `Fresh data loaded: ${response.count} incidents found`,
            {
              moveToTop: true,
              duration: 3000
            }
          );
        },
        error: (error) => {
          console.error('Error fetching fresh incident data:', error);
          this.notificationService.deleteNotification('fetching-data');
          this.notificationService.addDangerNotification(
            `Failed to fetch data: ${error.message || 'Unknown error'}`,
            {
              moveToTop: true,
              duration: 5000
            }
          );
        }
      });
  }

  // Method to create notification for specific incident by ID
  showIncidentNotification(incidentId: string): void {
    const latestResponse = this.latestApiResponse$.value;
    
    if (!latestResponse) {
      this.notificationService.addWarningNotification('No API data available', {
        moveToTop: true,
        duration: 3000
      });
      return;
    }

    const incident = latestResponse.rows.find(inc => inc.id === incidentId);
    
    if (!incident) {
      this.notificationService.addWarningNotification(`Incident ${incidentId} not found`, {
        moveToTop: true,
        duration: 3000
      });
      return;
    }

    const detailedContent = `Incident Details:
Name: ${incident.name}
Description: ${incident.description || 'No description'}
Status: ${incident.status}
Type: ${incident.type}
ID: ${incident.id}
Created: ${new Date(incident.createdAt).toLocaleString()}
Updated: ${new Date(incident.updatedAt).toLocaleString()}`;

    const notificationType = this.getIncidentNotificationType(incident.status);
    
    // Use switch statement for better type safety
    switch (notificationType) {
      case 'danger':
        this.notificationService.addDangerNotification(detailedContent, {
          id: `specific-incident-${incident.id}`,
          moveToTop: true,
          onGoLive: () => this.navigateToIncident(incident.id),
          replace: true
        });
        break;
        
      case 'warning':
        this.notificationService.addWarningNotification(detailedContent, {
          id: `specific-incident-${incident.id}`,
          moveToTop: true,
          onGoLive: () => this.navigateToIncident(incident.id),
          replace: true
        });
        break;
        
      case 'success':
        this.notificationService.addSuccessNotification(detailedContent, {
          id: `specific-incident-${incident.id}`,
          moveToTop: true,
          onGoLive: () => this.navigateToIncident(incident.id),
          replace: true
        });
        break;
        
      default:
        this.notificationService.addInfoNotification(detailedContent, {
          id: `specific-incident-${incident.id}`,
          moveToTop: true,
          onGoLive: () => this.navigateToIncident(incident.id),
          replace: true
        });
        break;
    }
  }

  private navigateToIncident(incidentId: string): void {
    
    // Instead of navigation, show detailed incident information
    //this.showIncidentDetails(incidentId);
  }

  // Add this method after the showIncidentDetails method (around line 500)
  private getIncidentNotificationType(status: string): 'info' | 'success' | 'warning' | 'danger' {
  switch (status?.toUpperCase()) {
    case 'CRITICAL':
    case 'EMERGENCY':
    case 'HIGH':
      return 'danger';        // RED background
    case 'ACTIVE':
    case 'PENDING':           // PENDING status
    case 'MEDIUM':
      return 'warning';       // ORANGE/AMBER background (like in your image)
    case 'RESOLVED':
    case 'CLOSED':
    case 'INACTIVE':
      return 'success';       // GREEN background
    case 'LOW':
    case 'INFO':
    default:
      return 'info';          // BLUE background
  }
}

  // Also add the missing handleNewIncident method
  private handleNewIncident(incident: Incident): void {
    // Emit to incidents stream
    this.incidents$.next(incident);
    const content = `New Incident: ${incident.name}`;
    const description = incident.description || 'No description available';
    const fullContent = `${incident.name}
Description: ${incident.description || 'No description'}
Status: ${incident.status}
Type: ${incident.type}
Created: ${incident.createdAt}
Updated: ${incident.updatedAt}`;
    // Get notification type based on incident status
    const notificationType = this.getIncidentNotificationType(incident.status);

    // Use the correct notification service methods with proper parameters
    switch (notificationType) {
      case 'danger':
        this.notificationService.addDangerNotification(fullContent, {
          id: `incident-${incident.id}`,
          moveToTop: true,
          onGoLive: () => this.navigateToIncident(incident.id),
          replace: false
        });
        break;
        
      case 'warning':
        this.notificationService.addWarningNotification(fullContent, {
          id: `incident-${incident.id}`,
          moveToTop: true,
          onGoLive: () => this.navigateToIncident(incident.id),
          replace: false
        });
        break;
        
      case 'success':
        this.notificationService.addSuccessNotification(fullContent, {
          id: `incident-${incident.id}`,
          moveToTop: false,
          onGoLive: () => this.navigateToIncident(incident.id),
          replace: false
        });
        break;
        
      default:
        this.notificationService.addInfoNotification(fullContent, {
          id: `incident-${incident.id}`,
          moveToTop: false,
          onGoLive: () => this.navigateToIncident(incident.id),
          replace: false
        });
        break;
    }
  }

  // Handle removed incidents - delete their notifications
  private handleRemovedIncident(incident: Incident): void {
    const notificationIds = [
      incident.id,
      `incident-${incident.id}`,
      `incident-update-${incident?.id}`,
      `specific-incident-${incident?.id}`
    ];
    
    notificationIds?.forEach(notificationId => {
      this.notificationService.deleteNotification(notificationId);
    });
  }

  // Also add the missing handleUpdatedIncident method
  private handleUpdatedIncident(incident: Incident): void {
    // Delete old notification with the original ID format used in handleNewIncident
    const oldNotificationId = `incident-${incident.id}`;
    this.notificationService.deleteNotification(oldNotificationId);

    // Also delete any other possible notification IDs for this incident
    const otherNotificationIds = [
      incident.id,
      `incident-update-${incident.id}`,
      `specific-incident-${incident.id}`
    ];
    otherNotificationIds.forEach(notificationId => {
      this.notificationService.deleteNotification(notificationId);
    });

    // Create new notification with updated status using the same ID format as handleNewIncident
    const createdDate = new Date(incident.createdAt);
    const updatedDate = new Date(incident.updatedAt);
    const fullContent = `${incident.name}
Description: ${incident.description || 'No description'}
Status: ${incident.status}
Type: ${incident.type}
Created: ${createdDate}
Updated: ${updatedDate}`;

    const notificationType = this.getIncidentNotificationType(incident.status);
    const isUrgent = incident.status === 'ACTIVE';

    switch (notificationType) {
      case 'danger':
        this.notificationService.addDangerNotification(fullContent, {
          id: `incident-${incident.id}`,
          moveToTop: isUrgent,
          onGoLive: () => this.navigateToIncident(incident.id),
          replace: false
        });
        break;

      case 'warning':
        this.notificationService.addWarningNotification(fullContent, {
          id: `incident-${incident.id}`,
          moveToTop: false,
          onGoLive: () => this.navigateToIncident(incident.id),
          replace: false
        });
        break;

      case 'success':
        this.notificationService.addSuccessNotification(fullContent, {
          id: `incident-${incident.id}`,
          moveToTop: false,
          onGoLive: () => this.navigateToIncident(incident.id),
          replace: false
        });
        break;

      default:
        this.notificationService.addInfoNotification(fullContent, {
          id: `incident-${incident.id}`,
          moveToTop: false,
          onGoLive: () => this.navigateToIncident(incident.id),
          replace: false
        });
        break;
    }
  }

  // Update refresh method to show notifications
  refresh(): void {
    this.fetchIncidentsAndNotify();
  }

  // Set polling interval
  setPollingInterval(intervalMs: number): void {
    this.pollingInterval = intervalMs;
    
    if (this.incidentPollingSubscription) {
      this.disconnect();
      this.connect();
    }
  }

  // Add a method to test the incident service connection
  testIncidentService(): void {
    
    this.incidentService
      .getIncidentFindAndCountAllFromOrganization()
      .subscribe({
        next: (response) => {
          this.notificationService.addSuccessNotification(
            `Test successful: Found ${response.count} incidents`,
            {
              moveToTop: true,
              duration: 3000
            }
          );
        },
        error: (error) => {
          console.error('Test failed:', error);
          this.notificationService.addDangerNotification(
            `Test failed: ${error.message || 'Unknown error'}`,
            {
              moveToTop: true,
              duration: 5000
            }
          );
        }
      });
  }

  // Add a method to get incident service status
  getIncidentServiceStatus(): Observable<IncidentResponse> {
    return this.incidentService.getIncidentFindAndCountAllFromOrganization();
  }

  // Optional: Add method to handle different response formats if needed
  private validateIncidentResponse(response: any): IncidentResponse {
    // Ensure response has the expected structure
    if (!response) {
      return { count: 0, rows: [] };
    }
    
    if (typeof response.count !== 'number') {
      response.count = Array.isArray(response.rows) ? response.rows.length : 0;
    }
    
    if (!Array.isArray(response.rows)) {
      response.rows = [];
    }
    
    return response as IncidentResponse;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnect();
  }
}